import {
  Case,
  CaseDocument,
  CaseDocumentWithPresignedUrl,
  CaseQAReview,
  Cases,
  ScreenRequest,
  Slide,
  SlideClassification,
  Specimen,
  User,
  UserContact,
} from "pwapi";

import { CaseDiagnosis, CptCode, CptInput } from "custom-models";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { mostRecentlyCreated } from "shared/utils/dates";

import { DiagnosisDialogProps } from "./DiagnosisDialog";

// Check if the case and user are in a state where they can Amend or Addend a finalized case.
export const canAmendOrAddend = (
  caseDetailObject: CaseDetailState,
  currentUser?: User
): boolean =>
  caseDetailObject.source === Case.source.PW &&
  caseDetailObject.status === Case.status.FINALIZED &&
  !!caseDetailObject.active_assignment?.id &&
  caseDetailObject.active_assignment.id === currentUser?.id;

//Check if the case and user are able to correct a finalized case.
export const canCorrectReport = (
  caseDetailObject: CaseDetailState,
  featureFlags: FeatureFlags,
  currentUser?: User
): boolean =>
  caseDetailObject.source === Case.source.PW &&
  caseDetailObject.status === Case.status.FINALIZED &&
  ((featureFlags.correctOwnReport &&
    caseDetailObject.active_assignment?.id === currentUser?.id) ||
    !!featureFlags.correctAnyReport);

// Check if a specific case is in an Amend or Addend state.
//   If not in either state, returns undefined.
export const isAmendOrAddend = (
  caseDetailObject: CaseDetailState,
  currentUser?: User
) => {
  const diagnosis = caseDetailObject.case_diagnosis?.find(
    (diag) =>
      (diag.source === CaseDiagnosis.source.ADDEND ||
        diag.source === CaseDiagnosis.source.AMEND) &&
      diag.user?.id === currentUser?.id &&
      !diag.is_completed
  );
  return canAmendOrAddend(caseDetailObject, currentUser)
    ? diagnosis?.source
    : undefined;
};

// Check if the case is currently in a report modification
export const isModifyingReport = (
  caseDetailObject: CaseDetailState,
  features: FeatureFlags,
  currentUser?: User
) => {
  const diagnosis = caseDetailObject.case_diagnosis?.find(
    (diag) =>
      (diag.source === CaseDiagnosis.source.ADDEND ||
        diag.source === CaseDiagnosis.source.AMEND ||
        diag.source === CaseDiagnosis.source.CORRECTION) &&
      diag.user?.id === currentUser?.id &&
      !diag.is_completed
  );

  return (diagnosis?.source === CaseDiagnosis.source.ADDEND ||
    diagnosis?.source === CaseDiagnosis.source.AMEND) &&
    canAmendOrAddend(caseDetailObject, currentUser)
    ? { source: diagnosis?.source }
    : diagnosis?.source === CaseDiagnosis.source.CORRECTION &&
      canCorrectReport(caseDetailObject, features, currentUser)
    ? { source: diagnosis?.source }
    : undefined;
};

// Check if case or user has the privileges to sign out the case.
export const canSignoutCase = (
  caseDetailObject: CaseDetailState | Cases,
  currentUser?: User,
  hasFeatureFlag?: boolean
): boolean => {
  return (
    !!hasFeatureFlag &&
    isDiagnosable(caseDetailObject) &&
    !!caseDetailObject.active_assignment?.id &&
    caseDetailObject.active_assignment?.id === currentUser?.id
  );
};

export const getReviewStatus = (
  caseDetailObject: CaseDetailState
): boolean | null => {
  if (!caseDetailObject.qa_reviews?.[0]) {
    return null;
  }

  if (!caseDetailObject.qa_reviews[0].reviewed) {
    return false;
  }

  return true;
};

export const specimenHasDeliverables = (pathCase: Case, specimenId: number) => {
  const blockIds =
    pathCase.blocks
      ?.filter((b) => b.specimen_id === specimenId)
      .map((b) => b.id) || [];
  const hasSlides = pathCase.slides?.some((s) =>
    blockIds.includes(s.block_obj_id)
  );
  if (hasSlides) return true;

  return pathCase.documents?.some((d) => d.specimen_id === specimenId);
};

export const specimenHasActiveDeliverables = (
  pathCase: Case,
  specimenId: number
) => {
  const blockIds =
    pathCase.blocks
      ?.filter((b) => b.specimen_id === specimenId)
      .map((b) => b.id) || [];
  const hasSlides = pathCase.slides?.some(
    (s) =>
      s.status === Slide.status.SUCCESS && blockIds.includes(s.block_obj_id)
  );
  if (hasSlides) return true;

  return pathCase.documents?.some(
    (d) =>
      d.status === CaseDocument.status.UPLOADED && d.specimen_id === specimenId
  );
};

export const isDiagnosable = (caseDetailObject: CaseDetailState | Cases) => {
  return (
    (caseDetailObject.status === Case.status.ACTIVE ||
      caseDetailObject.status === Case.status.WORKING) &&
    caseDetailObject.type !== Case.type.HISTORICAL
  );
};

// Check if the highest versioned diagnosis is a screen diagnosis
export const isScreened = (diagnoses: CaseDiagnosis[] = []) => {
  return (
    getHighestVersionDiagnosis(diagnoses)?.source ===
    CaseDiagnosis.source.SCREEN
  );
};

export const isSpecimenScreened = (
  diagnoses: CaseDiagnosis[] = [],
  specimen: Specimen
) => {
  return (
    getHighestVersionDiagnosis(
      diagnoses.filter((diag) => diag.specimen === specimen.id) || []
    )?.source === CaseDiagnosis.source.SCREEN
  );
};

// Note that this is not explictly !isDiagnosable(),
// ... because we have the interstitial status of `Case.status.LIS_PENDING`
export const isFinalized = (caseDetailObject: CaseDetailState) => {
  return (
    caseDetailObject.status === Case.status.FINALIZED ||
    caseDetailObject.status === Case.status.ARCHIVED
  );
};

export const hasFinalizedDiagnosis = (caseDetailObject: CaseDetailState) => {
  return caseDetailObject.case_diagnosis?.some(
    (d) => d.source === CaseDiagnosis.source.FINALIZE && d.is_completed
  );
};

export const wasSignedOutFromWPL = (caseDetailObject: CaseDetailState) => {
  return (
    caseDetailObject.source === Case.source.WPL &&
    isFinalized(caseDetailObject) &&
    !hasFinalizedDiagnosis(caseDetailObject) &&
    caseDetailObject.case_diagnosis?.some(
      (d) => d.source === CaseDiagnosis.source.LIS_FINALIZED
    )
  );
};

// Get the highest versioned diagnosis from the list of diagnoses
// Providing a source limits the diagnoses to the source provided
export const getHighestVersionDiagnosis = (
  diagnoses: CaseDiagnosis[],
  source?: CaseDiagnosis.source
): CaseDiagnosis | undefined => {
  const filteredDiags = diagnoses?.filter(
    (diag) =>
      diag &&
      diag.is_completed &&
      diag.version &&
      (!source || diag.source === source)
  );
  return filteredDiags && filteredDiags.length > 0
    ? filteredDiags.reduce(
        (prev, curr) =>
          (prev.version || -1) > (curr.version || -1) ? prev : curr,
        filteredDiags[0]
      )
    : undefined;
};

export const getMostRecentScreenRequest = (
  screenRequests: ScreenRequest[] = []
) => {
  const request = screenRequests.reduce(
    (a, b) => ((a.created_at || "") > (b.created_at || "") ? a : b),
    {} as ScreenRequest
  );
  return request?.created_at ? request : undefined;
};

export const specimenAndDiagnosis = ({
  caseDetailObject,
  currentSlideId, // needed for Specimen
  user,
}: DiagnosisDialogProps) => {
  const currSlideIndex = caseDetailObject.slides.findIndex(
    (s) => s.id === currentSlideId
  );
  const slide = caseDetailObject.slides[currSlideIndex];
  const block = caseDetailObject.blocks?.find(
    (b) => b.id === slide?.block_obj_id
  );
  const currentSpecimen = caseDetailObject.specimens?.find(
    (s) => s.id === block?.specimen_id || s.id === slide?.specimen_id
  );

  // const diagnoses: CaseDiagnosis[] | undefined = caseDetailObject?.case_diagnosis;
  const diagnoses: CaseDiagnosis[] | undefined =
    caseDetailObject?.case_diagnosis as CaseDiagnosis[] | undefined;

  const allSlidesViewed = caseDetailObject.slides.every(
    (s) => s.id === currentSlideId || s.was_previously_viewed
  );

  // const diagnosisBySpecimen: Map<number, CaseDiagnosis> = new Map();
  const diagnosisBySpecimen: Map<number, CaseDiagnosis> = new Map();
  (diagnoses || []).forEach((diag) => {
    const user_id = Number.isInteger(diag.user)
      ? diag.user
      : diag.user?.hasOwnProperty("id")
      ? diag.user.id
      : 0;

    if (user_id === user?.id) {
      // Filter out other people's diagnoses
      diagnosisBySpecimen.set(diag.specimen || 0, diag);
    }
  });
  const currentDiagnosis = diagnosisBySpecimen.get(currentSpecimen?.id || 0);

  const allAreDiagnosed = caseDetailObject.specimens?.every((specimen) =>
    diagnosisBySpecimen.get(specimen.id || 0)
  );

  return {
    currSlideIndex,
    currentSpecimen,
    diagnoses,
    allSlidesViewed,
    diagnosisBySpecimen,
    currentDiagnosis,
    allAreDiagnosed,
  };
};

export const slideClassification = (slide: SlideDetailState) => {
  return mostRecentlyCreated(
    slide.classifications?.filter(
      (s) => s.status === SlideClassification.status.SUCCEEDED
    )
  );
};
export const slideFromSpecimenClassification = (
  slide_id: number,
  specimenClassification: Array<SlideClassification>
) => {
  return specimenClassification.find((s) => s.id === slide_id);
};

export const getFinalizedReportCaseDocument = (caseDetail: CaseDetailState) => {
  return [...caseDetail.documents]
    .reverse()
    .find(
      (d) =>
        d.category === CaseDocumentWithPresignedUrl.category.CASE_REPORT &&
        d.status === CaseDocumentWithPresignedUrl.status.UPLOADED
    );
};

export const getLatestReportVersion = (
  caseDetail: CaseDetailState | CaseAdminDetail,
  category: CaseDocumentWithPresignedUrl.category
) => {
  return [...(caseDetail?.documents || [])]
    .reverse()
    .find(
      (d) =>
        d.category === category &&
        d.status === CaseDocumentWithPresignedUrl.status.UPLOADED
    );
};

export const isDigitizationCode = (cpt: CptInput | CptCode) => {
  const code: string | undefined =
    typeof cpt.code === "string" ? cpt.code : cpt.code?.code;
  return code?.endsWith("T");
};

export const validSpecimen = (specimens?: Specimen[]) => {
  return specimens?.every(
    (specimen) => specimen.clinical_history && specimen.gross_description
  );
};
export const validOrderingDoctor = (user?: UserContact) => {
  return user && user.email !== "tbd@pathologywatch.com";
};
export const validCaseDocuments = (
  documents: CaseDocumentWithPresignedUrl[],
  features?: FeatureFlags
) => {
  if (features?.skipDocCheck) return true;
  return documents.find(
    (d) => d.category === CaseDocumentWithPresignedUrl.category.REQUISITION_FORM
  );
};

export const validQAReview = (
  caseSource?: Case.source,
  qaReview?: CaseQAReview,
  status?: Case.status,
  features?: FeatureFlags
) => {
  if (
    !features?.qaReviews ||
    caseSource !== Case.source.PW ||
    (status && ![Case.status.ACTIVE, Case.status.WORKING].includes(status))
  )
    return true;
  return qaReview && qaReview.reviewed;
};

export const isCaseValid = (
  caseDetailObject: CaseDetailState,
  features?: FeatureFlags
) => {
  return (
    validSpecimen(caseDetailObject.specimens as Specimen[]) &&
    validOrderingDoctor(caseDetailObject.ordering_doctor) &&
    validCaseDocuments(caseDetailObject.documents, features) &&
    validQAReview(
      caseDetailObject.source,
      caseDetailObject.qa_reviews?.[0],
      caseDetailObject.status,
      features
    )
  );
};
export const invalidCaseReasons = (
  caseDetailObject: CaseDetailState,
  features?: FeatureFlags
) => {
  return [
    !validOrderingDoctor(caseDetailObject.ordering_doctor) &&
      "Missing Ordering Provider",
    !validSpecimen(caseDetailObject.specimens as Specimen[]) &&
      `Missing Specimen Information: ${caseDetailObject.specimens
        ?.filter((s) => !(s.clinical_history && s.gross_description))
        .map((s) => s?.name)}`,
    !validCaseDocuments(caseDetailObject.documents, features) &&
      "Missing Requisition Form",
    !!caseDetailObject.accession_prefix?.is_diagnostic &&
      !caseDetailObject.accessioning_lab?.settings?.skip_qa_review?.active &&
      !validQAReview(
        caseDetailObject.source,
        caseDetailObject.qa_reviews?.[0],
        caseDetailObject.status,
        features
      ) &&
      "Case has not been Reviewed by QA team",
  ].filter((r) => r);
};

export const softDiagnosisValidationReasons = (
  caseDetailObject: CaseDetailState,
  caseDiagnoses?: (CaseDiagnosis | undefined)[]
): string[] => {
  const specimensMissingCodes =
    caseDiagnoses
      ?.filter(
        (d) =>
          d &&
          (!d.icd10_code ||
            (!d.zsd_code && caseDetailObject.patient?.clinic?.modmed_clinic_id))
      )
      .map((d) => ({
        name: caseDetailObject.specimenDetail?.find((s) => s.id === d?.specimen)
          ?.name,
        missingZSD:
          !d?.zsd_code && caseDetailObject.patient?.clinic?.modmed_clinic_id,
        missingICD10: !d?.icd10_code,
      }))
      .filter((s) => s.name) || [];

  const specimensMissingZSD = specimensMissingCodes.filter((z) => z.missingZSD);
  const specimensMissingICD10 = specimensMissingCodes.filter(
    (z) => z.missingICD10
  );

  return [
    ...(specimensMissingZSD?.length
      ? [
          `Missing ZSD Code in ${
            specimensMissingZSD.length > 1
              ? "diagnoses for specimens"
              : "diagnosis for specimen"
          } ${createListString(
            specimensMissingZSD.map((s) => s?.name) as string[]
          )}`,
        ]
      : []),
    ...(specimensMissingICD10?.length
      ? [
          `Missing ICD10 Code in ${
            specimensMissingICD10.length > 1
              ? "diagnoses for specimens"
              : "diagnosis for specimen"
          } ${createListString(
            specimensMissingICD10.map((s) => s?.name) as string[]
          )}`,
        ]
      : []),
  ];
};

/**
 * This method is made to make sure that a string array is reduced to a grammatically correct list
 * Examples
 *  ['hello'] returns 'hello'
 *  ['apple', 'lemon'] returns 'apple and lemon'
 *  ['apple', 'lemon', 'banana'] returns 'apple, lemon, and banana'
 */
export const createListString = (arr: string[]) => {
  return arr.reduce(
    (acc, prev, index, arr) =>
      `${acc}${
        arr.length === 1
          ? prev
          : index === arr.length - 2
          ? `${prev}${arr.length > 2 ? "," : ""} and `
          : index === arr.length - 1
          ? `${prev}`
          : `${prev}, `
      }`,
    ""
  );
};

type DisclaimersObject = {
  type: string;
  disclaimer: string;
};

export const findScreenDisclaimer = (
  disclaimersArr: { type?: string; disclaimer?: string }[]
): DisclaimersObject | undefined => {
  const found = disclaimersArr.find((d) => d.type === "screen");
  if (
    found &&
    typeof found.type === "string" &&
    typeof found.disclaimer === "string"
  ) {
    return { type: found.type, disclaimer: found.disclaimer };
  }
  return undefined;
};
